import Service from '@ember/service';
import { allSettled, didCancel, Task, task } from 'ember-concurrency';
import { service } from '@ember/service';

import type RouterService from '@ember/routing/router-service';
import { type Transition } from 'admin-panel/utils/type-utils';

type RouterWithLocation = RouterService & {
  location?: {
    getURL?: () => string;
    replaceURL?: (url: string) => void;
  };
};

export interface ModalContext {
  exitAnimationTask?: Task<unknown, never[]>;
  preservesOnQueryParamChange?: boolean;
  canLeave?: () => boolean | Promise<boolean>;
}

export default class ModalManagerService extends Service {
  @service
  declare router: RouterService;

  private _abortInProgress = false;

  private _boundTransitionHandler:
    | ((transition: Transition) => Promise<void>)
    | null = null;

  private _modals: Set<ModalContext> = new Set();

  get hasOpenModals() {
    return !!this._modals.size;
  }

  get hasQueryParamAwareModals() {
    return [...this._modals].some(
      (context) => context.preservesOnQueryParamChange
    );
  }

  register(context: ModalContext) {
    this._modals.add(context);
  }

  unregister(context: ModalContext) {
    this._modals.delete(context);
  }

  async closeOpenModals() {
    try {
      await this.allExitAnimationsTask.perform();
    } catch (error) {
      if (!didCancel(error)) throw error;
    }
  }

  setupTransitionHandler() {
    this._boundTransitionHandler = this.handleTransition.bind(this);
    this.router.on('routeWillChange', this._boundTransitionHandler);
  }

  removeTransitionHandler() {
    if (this._boundTransitionHandler) {
      this.router.off('routeWillChange', this._boundTransitionHandler);
      this._boundTransitionHandler = null;
    }
  }

  async handleTransition(transition: Transition) {
    if (!transition.from) return;

    if (
      isSameRoute(transition) &&
      (!this.hasQueryParamAwareModals || hasSameQueryParams(transition))
    ) {
      return;
    }

    if (!this.hasOpenModals || this._abortInProgress) return;

    try {
      this._abortInProgress = true;

      transition.abort();

      for (const context of [...this._modals]) {
        if ((await context.canLeave?.()) === false) {
          this.restoreURL();

          return;
        }
      }

      if (!this.shouldPreserveOpenModals(transition)) {
        await this.closeOpenModals();
      }

      transition.retry();
    } finally {
      this._abortInProgress = false;
    }
  }

  private restoreURL() {
    if (this.isDestroying || this.isDestroyed) return;

    const { currentURL } = this.router;

    if (currentURL === null) return;

    const { location } = this.router as RouterWithLocation;

    if (location?.getURL?.() === currentURL) return;

    location?.replaceURL?.(currentURL);
  }

  private shouldPreserveOpenModals(transition: Transition) {
    return isSameRoute(transition) && this.hasQueryParamAwareModals;
  }

  allExitAnimationsTask = task({ drop: true }, async () => {
    await allSettled(
      [...this._modals].map((context) => context.exitAnimationTask?.perform())
    );
  });
}

function isSameRoute(transition: Transition) {
  return (
    !!transition.from?.name &&
    transition.from.name === transition.to.name &&
    hasSameRouteParams(transition)
  );
}

function hasSameRouteParams(transition: Transition) {
  const fromParams = routeParamsFor(transition.from);
  const toParams = routeParamsFor(transition.to);
  const keys = new Set([...Object.keys(fromParams), ...Object.keys(toParams)]);

  for (const key of keys) {
    if (fromParams[key] !== toParams[key]) {
      return false;
    }
  }

  return true;
}

type RouteInfoWithParams = {
  params?: Record<string, string | undefined>;
  parent?: RouteInfoWithParams | null;
};

function routeParamsFor(
  routeInfo: RouteInfoWithParams | null | undefined
): Record<string, string | undefined> {
  if (!routeInfo) return {};

  return {
    ...routeParamsFor(routeInfo.parent),
    ...(routeInfo.params ?? {}),
  };
}

function hasSameQueryParams(transition: Transition) {
  const fromQueryParams = transition.from?.queryParams || {};
  const toQueryParams = transition.to.queryParams || {};
  const keys = new Set([
    ...Object.keys(fromQueryParams),
    ...Object.keys(toQueryParams),
  ]);

  for (const key of keys) {
    if (fromQueryParams[key] !== toQueryParams[key]) {
      return false;
    }
  }

  return true;
}

declare module '@ember/service' {
  interface Registry {
    'modal-manager': ModalManagerService;
  }
}
